import { matchRoutes } from 'react-router-dom';
import { AfterHourReferralRulesList } from '../../components/feature/afterHourReferralRules/afterHourReferralRuleList';
import { CampaignAdhocForm } from '../../components/feature/communications/campaigns/adhocs/campaignAdhocForm';
import { CampaignAdhocList } from '../../components/feature/communications/campaigns/adhocs/campaignAdhocList';
import { CampaignAdhocView } from '../../components/feature/communications/campaigns/adhocs/campaignAdhocView';
import { CampaignAdhocTemplateForm } from '../../components/feature/communications/campaigns/adhocs/templates/campaignAdhocTemplateForm';
import { CampaignAdhocTemplateList } from '../../components/feature/communications/campaigns/adhocs/templates/campaignAdhocTemplateList';
import { CampaignAdhocTemplateView } from '../../components/feature/communications/campaigns/adhocs/templates/campaignAdhocTemplateView';
import { CommunicationLogList } from '../../components/feature/communications/logs/communicationLogList';
import { ReportView } from '../../components/feature/communications/reports/reportView';
import { ReportsList } from '../../components/feature/communications/reports/reportsList';
import { Dashboard } from '../../components/feature/dashboard/dashboard';
import { BranchForm } from '../../components/feature/locations/branches/branchForm';
import { BranchList } from '../../components/feature/locations/branches/branchList';
import { BranchView } from '../../components/feature/locations/branches/branchView';
import { ZipImport } from '../../components/feature/locations/branches/import';
import { ZipcodeForm } from '../../components/feature/locations/zipcodes/zipcodeForm';
import { ZipcodeList } from '../../components/feature/locations/zipcodes/zipcodeList';
import { ZipcodeView } from '../../components/feature/locations/zipcodes/zipcodeView';
import { SystemForm } from '../../components/feature/systemAccess/systemForm';
import { SystemList } from '../../components/feature/systemAccess/systemList';
import { SystemView } from '../../components/feature/systemAccess/systemView';
import { UserList } from '../../components/feature/users/userList';
import { UserView } from '../../components/feature/users/userView';
import { PageNotFound } from '../../components/shared/error/pageNotFound';
import { AFTER_HOUR_REFERRAL_RULE, BRANCH, CAMPAIGN_ADHOC, CAMPAIGN_ADHOC_TEMPLATE, COMMUNICATION_LOG, REPORT, SYSTEM, USER, ZIPCODE } from '../../permissions';
import { ReactComponent as AfterHourReferralRuleIcon } from './../../assets/icons/after-hours-referral-rules.svg';
import { ReactComponent as BranchesIcon } from './../../assets/icons/branch.svg';
import { ReactComponent as CampaignIcon } from './../../assets/icons/campaign.svg';
import { ReactComponent as CommunicationsIcon } from './../../assets/icons/communications.svg';
import { ReactComponent as DashboardIcon } from './../../assets/icons/dashboard.svg';
import { ReactComponent as LocationsIcon } from './../../assets/icons/locations.svg';
import { ReactComponent as NoteIcon } from './../../assets/icons/note.svg';
import { ReactComponent as CommunicationLogsIcon } from './../../assets/icons/communication-logs.svg';
import { ReactComponent as ReportIcon } from './../../assets/icons/report.svg';
import { ReactComponent as SystemIcon } from './../../assets/icons/systems.svg';
import { ReactComponent as UsersIcon } from './../../assets/icons/users.svg';
import { ReactComponent as ZipCodesIcon } from './../../assets/icons/zipcode.svg';


export const iconMap = {
    'Branch': BranchesIcon,
    'Zip Code': ZipCodesIcon,
    'Zipcode': ZipCodesIcon,
    'System': SystemIcon,
    'User': UsersIcon,
    'Campaign': CampaignIcon,
    'Adhoc Campaign': CampaignIcon,
    'CampaignAdhoc': CampaignIcon,
    'Campaign Template': NoteIcon,
    'Adhoc Campaign Template': NoteIcon,
    'CampaignAdhocTemplate': NoteIcon,
    'Campaign Log': CommunicationLogsIcon,
    'CampaignLog': CommunicationLogsIcon,
    'AfterHourReferralRule': AfterHourReferralRuleIcon,
    'Report': ReportIcon,
};

export const knownEntities = [
    'Branch',
    'Zipcode',
    'System',
    'User',
    'Report',
    'CampaignAdhoc',
    'CampaignAdhocTemplate',
    'CampaignLog',
    'AfterHourReferralRule',
];


/**
 * A route definition.
 * @typedef {Object} Route
 * @property {(function(): JSX.Element)} [featureComponent] If given, will render when navigated, if not given then it's not routable. 
 * @property {string} [name] Name, used in navs
 * @property {(JSX.Element)} [icon] Used in the navs
 * @property {boolean} [showQuickSearch=false] Quick search is the search at the top of the screen in the nav
 * @property {boolean} [showPathNav=false] Path nav is the path nav that makes an intractable url
 * @property {boolean} [showInNav=false] Show in the primary nav. All parents must be rendered
 * @property {string[]} [permissions=[]] Permissions to require
 * @property {Routes} [children=[]] Routes and permissions are inherited
 * @property {Route|null} [parent=null] Route Parent. Null unless in the routes array.
 */

/**
 * @typedef {Object.<string, Route>} Routes
 */

/** @type {Routes} */
export const routes = {
    '/': {
        featureComponent: Dashboard,
        name: 'Home',
        icon: <DashboardIcon />,
        showQuickSearch: false,
        showInNav: true,
    },
    '/locations': {
        name: 'Locations',
        icon: <LocationsIcon />,
        showInNav: true,
        children: {
            '/branches': {
                featureComponent: BranchList,
                name: 'Branches',
                icon: <BranchesIcon />,
                showQuickSearch: false,
                showPathNav: true,
                showInNav: true,
                permissions: [BRANCH.READ],
                children: {
                    '/:id': {
                        featureComponent: BranchView,
                        showQuickSearch: true,
                        showPathNav: true,
                        children: {
                            '/edit': {
                                featureComponent: BranchForm,
                                showQuickSearch: true,
                                showPathNav: true,
                                permissions: [BRANCH.WRITE],
                            },
                            '/import': {
                                featureComponent: ZipImport,
                                showQuickSearch: true,
                                showPathNav: true,
                                permissions: [BRANCH.WRITE],
                            }
                        }
                    },
                    '/add': {
                        featureComponent: BranchForm,
                        showQuickSearch: true,
                        showPathNav: true,
                        permissions: [BRANCH.WRITE],
                    }
                }
            },
            '/zipcodes': {
                featureComponent: ZipcodeList,
                name: 'Zip Codes',
                icon: <ZipCodesIcon />,
                showQuickSearch: false,
                showPathNav: true,
                showInNav: true,
                permissions: [ZIPCODE.READ],
                children: {
                    '/:id': {
                        featureComponent: ZipcodeView,
                        showQuickSearch: true,
                        showPathNav: true,
                        children: {
                            '/edit': {
                                featureComponent: ZipcodeForm,
                                showQuickSearch: true,
                                showPathNav: true,
                                permissions: [ZIPCODE.WRITE],
                            }
                        }
                    },
                    '/add': {
                        featureComponent: ZipcodeForm,
                        showQuickSearch: true,
                        showPathNav: true,
                        permissions: [ZIPCODE.WRITE],
                    },
                }
            },
        }
    },
    '/afterHourReferralRules': {
        featureComponent: AfterHourReferralRulesList,
        name: 'After Hour Referral Rules',
        icon: <AfterHourReferralRuleIcon />,
        showQuickSearch: false,
        showPathNav: true,
        showInNav: true,
        permissions: [AFTER_HOUR_REFERRAL_RULE.READ],
    },
    '/systemAccess': {
        featureComponent: SystemList,
        name: 'System Access',
        icon: <SystemIcon />,
        showQuickSearch: false,
        showPathNav: true,
        showInNav: true,
        permissions: [SYSTEM.READ],
        children: {
            '/:id': {
                featureComponent: SystemView,
                showQuickSearch: true,
                showPathNav: true,
                children: {
                    '/edit': {
                        featureComponent: SystemForm,
                        showQuickSearch: true,
                        showPathNav: true,
                        permissions: [SYSTEM.WRITE],
                    }
                }
            },
            '/add': {
                featureComponent: SystemForm,
                showQuickSearch: true,
                showPathNav: true,
                permissions: [SYSTEM.WRITE],
            },
        }
    },
    '/communications': {
        name: 'Communications',
        icon: <CommunicationsIcon />,
        showInNav: true,
        children: {
            '/logs': {
                featureComponent: CommunicationLogList,
                name: 'Logs',
                icon: <CommunicationLogsIcon />,
                showPathNav: true,
                showInNav: true,
                permissions: [COMMUNICATION_LOG.READ],
                // children: {
                //     '/:id': {
                //         featureComponent: CommunicationLogView,
                //         showQuickSearch: true,
                //         showPathNav: true,
                //     },
                // }
            },
            '/campaigns/templates': {
                featureComponent: CampaignAdhocTemplateList,
                name: 'Campaign Templates',
                icon: <NoteIcon />,
                showPathNav: true,
                showInNav: true,
                permissions: [CAMPAIGN_ADHOC.READ, CAMPAIGN_ADHOC_TEMPLATE.READ],
                children: {
                    '/:id': {
                        featureComponent: CampaignAdhocTemplateView,
                        showQuickSearch: true,
                        showPathNav: true,
                        children: {
                            '/edit': {
                                featureComponent: CampaignAdhocTemplateForm,
                                showQuickSearch: true,
                                showPathNav: true,
                                permissions: [CAMPAIGN_ADHOC.WRITE, CAMPAIGN_ADHOC_TEMPLATE.WRITE],
                            }
                        }
                    },
                    '/add': {
                        featureComponent: CampaignAdhocTemplateForm,
                        showQuickSearch: true,
                        showPathNav: true,
                        permissions: [CAMPAIGN_ADHOC.WRITE, CAMPAIGN_ADHOC_TEMPLATE.WRITE],
                    },
                }
            },
            '/campaigns': {
                featureComponent: CampaignAdhocList,
                name: 'Campaigns',
                icon: <CampaignIcon />,
                showPathNav: true,
                showInNav: true,
                permissions: [CAMPAIGN_ADHOC.READ],
                children: {
                    '/:id': {
                        featureComponent: CampaignAdhocView,
                        showQuickSearch: true,
                        showPathNav: true,
                    },
                    '/add': {
                        featureComponent: CampaignAdhocForm,
                        showQuickSearch: true,
                        showPathNav: true,
                        permissions: [CAMPAIGN_ADHOC.WRITE],
                    },
                }
            }
        }
    },
    '/reports': {
        featureComponent: ReportsList,
        name: 'Reports',
        icon: <ReportIcon />,
        showPathNav: true,
        showInNav: true,
        permissions: [REPORT.WRITE],
        children: {
            '/:id': {
                featureComponent: ReportView,
                showQuickSearch: true,
                showPathNav: true,
            },
        }
    },
    '/users': {
        featureComponent: UserList,
        name: 'Users',
        icon: <UsersIcon />,
        showInNav: true,
        showPathNav: true,
        permissions: [USER.READ],
        children: {
            '/:id': {
                featureComponent: UserView,
                showQuickSearch: true,
                showPathNav: true,
            }
        }
    },
    '*': {
        featureComponent: PageNotFound
    }
};

const convertToArray = (obj, parent = null) => {
    return Object.entries(obj).map((r, i) => {
        let x = [r[0], {
            ...r[1],
            parent: parent,
            index: i,
        }];

        if (r[1].children)
            x[1].children = convertToArray(r[1].children, x[1]);

        return x;
    });
}


export const routesArray = convertToArray(routes);
export const routesForReactRouter = routesArray.reduce((a, b) => {
    const loop = (obj, baseRoute = []) => {
        let route = [...baseRoute, obj[0]];
        a.push({path: route.join(''), routeInfo: obj, routePath: route});

        for (let i = 0; i < obj[1].children?.length ?? 0; i++)
            loop(obj[1].children[i], route);
    }

    loop(b);

    return a;
}, []);

export const getBestLocation = (path, routes = routesArray) => {
    let bestMatch = {
        /** @type number[] */
        index: [],
        /** @type string[] */
        route: [],
        matchChars: 0
    };

    const compare = (routeBase = [], array = routes, indexArr = []) => {
        for (let i = 0; i < array.length; i++) {
            const r = [...routeBase, array[i][0]];
            const rLen = r.join('').length;

            if (path.startsWith(r.join('')) && rLen > bestMatch.matchChars) {
                bestMatch.index = [...indexArr, i];
                bestMatch.route = r;
                bestMatch.matchChars = rLen;
            }

            if (array[i][1].children?.length > 0) {
                compare(r, array[i][1].children, [...indexArr, i]);
            }
        }
    }

    compare();

    return {
        indexArr: bestMatch.index,
        route: bestMatch.route,
    }
}


export const routerCompare = (location) => {
    // Get all possible routes
    return matchRoutes(
        routesForReactRouter,
        location
    );
}

export const navigateRouteArray = arr => 
    arr.reduce((a, b, i) => (i === arr.length - 1) ? a[b] : a[b].children, routes);



/**
 * Returns `true` if the route in question is a child or the base route (does the question route need the base route to be shown)
 * @param {Route} baseRoute The base route
 * @param {Route} questionRoute The route in question
 * @returns {boolean}
 */
export const isReliant = (baseRoute, questionRoute) => {

    if (baseRoute === questionRoute) return true;

    const getParents = parent => {
        let parents = [parent];
        while (parents[parents.length - 1] !== null) {
            parents.push(parents[parents.length - 1].parent);
        }

        return parents;
    }

    // Get the parents orders from the top down
    let baseParents = getParents(baseRoute).reverse();
    let questionParents = getParents(questionRoute).reverse();

    for (let i = 0; i < baseParents.length; i++) {
        if (baseParents[i] !== questionParents[i])
            return false;
    }

    return true;
}

/**
 * Returns the animation direction when transitioning between routes
 * @returns {'none'|'up'|'down'|'left'|'right'}
 */
export const getRouteAnimationDirection = (oldRoute, newRoute) => {

    if (oldRoute === newRoute) return 'none';

    // Collect all the parents for easier comparison
    const getParents = (parent) => {
        let parents = [parent];
        while (parents[parents.length - 1] !== null) {
            parents.push(parents[parents.length - 1].parent);
        }

        return parents;
    }

    let oldParents = getParents(oldRoute);
    let newParents = getParents(newRoute);

    for (let i = 0; i < newParents.length; i++) {
        // New route is a member of old route
        if (newParents[i] === oldRoute) return 'left';
    }

    for (let i = 0; i < oldParents.length; i++) {
        // Old route is a member of new route
        if (oldParents[i] === newRoute) return 'right';
    }

    // Routes are not members of one another, check if it needs to move up or down
    for (let i = 0; i < newParents.length; i++) {
        for (let e = 0; e < oldParents.length; e++) {
            if (newParents[i] === oldParents[e]) {
                return newParents[i - 1].index > oldParents[e - 1].index ? 'down' : 'up';
            }
        }
    }

    return 'none';
}